import { combine, withCallbacks, withContext } from 'react-hooks-combine'
import { withFormModel } from 'shared/hooks'
import { ServicesContext } from 'services/context'
import debounce from 'lodash/debounce'

import EditTriggerFormComponent from './component'
import { formModel } from './form.model'
import { enumToOptions } from './helpers'
import { toTriggerModel } from './mapping'

const getQueryParams = (search, tournamentId) => {
  const queryParams = {}

  if (tournamentId) {
    queryParams.tournamentId = tournamentId
  }

  if (search) {
    queryParams.search = search
  }

  return queryParams
}

const getFormEventWithAthletes = (formEvent, parsedEventId) => {
  if (
    formEvent &&
    formEvent.id === parsedEventId &&
    ((formEvent.teamOne?.athletesIds && formEvent.teamOne.athletesIds.length > 0) ||
      (formEvent.teamTwo?.athletesIds && formEvent.teamTwo.athletesIds.length > 0))
  ) {
    return formEvent
  }

  return null
}

const queryEventById = async (eventService, parsedEventId) => {
  try {
    const eventsResponse = await eventService.query({ id: parsedEventId })

    if (eventsResponse?.content?.length > 0) {
      return eventsResponse.content.find((event) => event.id === parsedEventId) || null
    }
  } catch (error) {
    console.error('Error querying event service:', error)
  }

  return null
}

const getAthleteIdsFromEvent = (event) => {
  const athleteIds = []

  if (event.teamOne && Array.isArray(event.teamOne.athletesIds)) {
    athleteIds.push(...event.teamOne.athletesIds)
  }

  if (event.teamTwo && Array.isArray(event.teamTwo.athletesIds)) {
    athleteIds.push(...event.teamTwo.athletesIds)
  }

  return athleteIds
}

const getAthleteBatches = (athleteIds, batchSize = 5) => {
  const uniqueAthleteIds = [...new Set(athleteIds)]
  const batches = []

  for (let i = 0; i < uniqueAthleteIds.length; i += batchSize) {
    batches.push(uniqueAthleteIds.slice(i, i + batchSize))
  }

  return batches
}

const fetchAthletesMapByBatch = async (adminAthletesService, athleteIds) => {
  const athletesMap = {}
  const batches = getAthleteBatches(athleteIds)

  for (const batch of batches) {
    const batchPromises = batch.map((id) =>
      adminAthletesService
        .getAthleteInfoById(id)
        .then((athlete) => {
          athletesMap[id] = athlete
          return athlete
        })
        .catch((err) => {
          console.error(`Error fetching athlete ${id}:`, err)
          return null
        })
    )

    await Promise.allSettled(batchPromises)
  }

  return athletesMap
}

const addFullNameToAthletes = (athletes) =>
  athletes.map((athlete) => {
    const firstName = athlete.name || ''
    const lastName = athlete.surname || ''

    return {
      ...athlete,
      fullName: `${firstName} ${lastName}`.trim(),
    }
  })

const filterAthletesBySearch = (athletes, search) => {
  if (!search || search.length === 0) {
    return athletes
  }

  const searchLower = search.toLowerCase()

  return athletes.filter((athlete) =>
    athlete.fullName.toLowerCase().includes(searchLower)
  )
}

export const EditTriggerForm = combine(
  withContext(
    'eventService',
    ServicesContext,
    ({ eventService }) => eventService
  ),
  withContext(
    'triggerService',
    ServicesContext,
    ({ triggerService }) => triggerService
  ),
  withContext(
    'adminTournamentService',
    ServicesContext,
    ({ adminTournamentService }) => adminTournamentService
  ),
  withContext(
    'adminAthletesService',
    ServicesContext,
    ({ adminAthletesService }) => adminAthletesService
  ),
  withContext(
    'adminEventService',
    ServicesContext,
    ({ adminEventService }) => adminEventService
  ),
  withFormModel(formModel, { id: 'trigger-edit-form' }),
  withCallbacks(
    {
      getOptionLabel:
        () =>
          ({ name }) =>
            name,
      getOptionValue:
        () =>
          ({ id }) =>
            id,

      getEventLabel:
        () =>
          ({ matchName }) =>
            matchName,
      getEventValue:
        () =>
          ({ id }) =>
            id,

      getAthleteLabel:
        () => {
          // Create a cache for athlete labels to prevent excessive re-renders
          const labelCache = new Map();

          return (athlete) => {
            // Handle null/undefined case
            if (!athlete) return '';

            // Use cached value if available for better performance
            if (athlete.id && labelCache.has(athlete.id)) {
              return labelCache.get(athlete.id);
            }

            // Check if athlete has expected structure
            if (typeof athlete !== 'object') {
              return 'Invalid athlete';
            }

            // Build a formatted name with team info if available
            try {
              const firstName = athlete.name || '';
              const lastName = athlete.surname || '';
              let fullName = `${firstName} ${lastName}`.trim();

              // Add team info for better context
              if (athlete.teamName) {
                fullName += ` (${athlete.teamName})`;
              } else if (athlete.team?.name) {
                fullName += ` (${athlete.team.name})`;
              }

              const label = fullName || (athlete.id ? `Athlete #${athlete.id}` : 'Unnamed athlete');

              // Cache the result
              if (athlete.id) {
                labelCache.set(athlete.id, label);
              }

              return label;
            } catch (error) {
              return 'Error formatting name';
            }
          };
        },
      getAthleteValue:
        () =>
          (athlete) => {
            // Handle null/undefined case
            if (!athlete) return '';

            // Check if athlete has expected structure
            if (typeof athlete !== 'object') {
              return '';
            }

            // Try to get id safely
            try {
              return athlete.id ? athlete.id.toString() : '';
            } catch (error) {
              return '';
            }
          },

      loadEvents:
        ({ eventService }) =>
          async (search, tournamentId) => {
            try {
              if (!tournamentId) {
                return []
              }

              const response = await eventService.query(
                getQueryParams(search, tournamentId)
              )

              return response?.content || []
            } catch (error) {
              console.error('Error loading events:', error)
              return []
            }
          },

      loadAthletes:
        ({ adminAthletesService, eventService, form }) =>
          async (search, eventId) => {
            try {
              if (!eventId) {
                return []
              }

              const parsedEventId = Number.parseInt(eventId, 10)
              const formEvent = form.fields.event.value
              const event =
                getFormEventWithAthletes(formEvent, parsedEventId) ||
                (await queryEventById(eventService, parsedEventId))

              if (!event) {
                return []
              }

              const athleteIds = getAthleteIdsFromEvent(event)

              if (athleteIds.length === 0) {
                return []
              }

              const athletesMap = await fetchAthletesMapByBatch(
                adminAthletesService,
                athleteIds
              )

              const athletes = filterAthletesBySearch(
                addFullNameToAthletes(Object.values(athletesMap).filter(Boolean)),
                search
              )

              return athletes.sort((a, b) => a.fullName.localeCompare(b.fullName))
            } catch (error) {
              console.error('Error in loadAthletes:', error)
              return []
            }
          },

      loadTournaments:
        ({ adminTournamentService }) => {
          // Create a debounced search function to avoid multiple requests
          const debouncedSearch = debounce(async (search, callback) => {
            try {
              const requestParams = {
                page: 0,
                size: 5,
                tournamentName: search,
                includePassedMatches: true
              };

              const { content } = await adminTournamentService.tournamentsSearch(requestParams);

              // Filter out tournaments where end date is in the past
              const now = new Date();
              const filteredContent = content ? content.filter(tournament => {
                // Check if tournament has endTime
                if (!tournament.endTime) return true;

                const endDate = new Date(tournament.endTime);
                return endDate > now;
              }) : [];

              callback(filteredContent);
            } catch (error) {
              console.error('Search error:', error);
              callback([]);
            }
          }, 300);

          // Return a function that uses the debounced search
          return (search) => {
            return new Promise((resolve) => {
              if (!search || search.length < 2) {
                resolve([]);
                return;
              }
              debouncedSearch(search, resolve);
            });
          };
        },

      loadTriggerCategories:
        ({ triggerService }) =>
          () =>
            triggerService.queryCategories().then(enumToOptions),

      loadTriggerSubcategories:
        ({ triggerService }) =>
          () =>
            triggerService.querySubcategories().then(enumToOptions),

      loadTriggerConditions:
        ({ triggerService }) =>
          () =>
            triggerService.queryConditions().then(enumToOptions),

      onChangeTournament: {
        deps: ['onChange'],
        func:
          ({ form }) =>
            async (tournament) => {
              // Handle null/cleared value
              if (!tournament) {
                form.fields.tournamentId.onChange({ target: { value: null } });
                // Clear event selection when tournament is cleared
                form.fields.event.onChange({ target: { value: null } });
                // Clear athlete selection when tournament is cleared
                form.fields.athleteId.onChange({ target: { value: null } });
                return;
              }

              // If we get here, we should have a valid tournament object
              if (typeof tournament !== 'object' || !tournament.id) {
                return;
              }

              // Update tournamentId with the tournament object
              form.fields.tournamentId.onChange({ target: { value: tournament } });

              // Clear event selection when tournament changes
              form.fields.event.onChange({ target: { value: null } });
              // Clear athlete selection when tournament changes
              form.fields.athleteId.onChange({ target: { value: null } });
            },
      },

      onChangeEvent: {
        deps: ['onChange'],
        func:
          ({ form }) =>
            async (event) => {
              // Handle null/cleared value
              if (!event) {
                form.fields.event.onChange({ target: { value: null } });
                // Clear athlete selection when event is cleared
                form.fields.athleteId.onChange({ target: { value: null } });
                return;
              }

              // If we get here, we should have a valid event object
              if (typeof event !== 'object' || !event.id) {
                return;
              }

              // Update event with the event object
              form.fields.event.onChange({ target: { value: event } });

              // Clear athlete selection when event changes
              form.fields.athleteId.onChange({ target: { value: null } });
            },
      },

      onChangeAthlete: {
        deps: ['onChange'],
        func:
          ({ form }) =>
            async (athlete) => {
              // Handle null/cleared value
              if (!athlete) {
                form.fields.athleteId.onChange({ target: { value: null } });
                return;
              }

              // If we get here, we should have a valid athlete object
              if (typeof athlete !== 'object' || !athlete.id) {
                return;
              }

              // Update athleteId with the athlete object
              form.fields.athleteId.onChange({ target: { value: athlete } });
            },
      },

      onSubmitRequest: {
        deps: ['onSubmit'],
        func:
          (_state, { onSubmit }) =>
            (form) => {
              return onSubmit(toTriggerModel(form))
            },
      },
    },
    []
  )
)(EditTriggerFormComponent)
